'use client';

import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import styles from './action-dropdown.module.scss';

interface ActionItem {
  key?: string;
  label: string;
  icon?: React.ReactElement;
  onClick?: () => void;
  title?: string;
  danger?: boolean;
  show?: boolean;
}

interface DividerItem {
  type: 'divider';
  key?: string;
}

type DropdownItem = ActionItem | DividerItem;

interface ThreeDotsIconProps {
  className?: string;
}

interface ActionDropdownProps {
  actions: ActionItem[];
  className?: string;
  onOpenChange?: (isOpen: boolean) => void;
  menuStyle?: React.CSSProperties;
}

const ThreeDotsIcon: React.FC<ThreeDotsIconProps> = ({ className }) => (
  <div className={classNames(styles['three-dots-icon'], className)}>
    <span />
    <span />
    <span />
  </div>
);

const ActionDropdown: React.FC<ActionDropdownProps> = ({
  actions,
  className,
  onOpenChange,
  menuStyle,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Notify parent of open state changes
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action: ActionItem) => {
    if (action.onClick && typeof action.onClick === 'function') {
      action.onClick();
    }
    // Don't close dropdown for display-only items
    if (action.onClick) {
      setIsOpen(false);
    }
  };

  // Filter out actions that shouldn't be shown and add dividers before danger actions
  const visibleActions = actions.filter((action) => action.show !== false);

  // Create dropdown items with dividers before danger actions
  const dropdownItems: DropdownItem[] = [];
  let hasNonDangerAction = false;

  visibleActions.forEach((action) => {
    if (action.danger) {
      // Add divider before first danger action if there are non-danger actions before it
      if (
        hasNonDangerAction &&
        !dropdownItems.some((item) => 'type' in item && item.type === 'divider')
      ) {
        dropdownItems.push({ type: 'divider', key: 'danger-divider' });
      }
    } else {
      hasNonDangerAction = true;
    }
    dropdownItems.push(action);
  });

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames(styles['action-dropdown'], className)}
      ref={dropdownRef}
    >
      <button
        className={styles['action-dropdown__trigger']}
        onClick={toggleDropdown}
        title="More actions"
        type="button"
      >
        <ThreeDotsIcon />
      </button>

      {isOpen && (
        <div className={styles['action-dropdown__menu']} style={menuStyle}>
          {dropdownItems.map((item, index) => {
            if ('type' in item && item.type === 'divider') {
              return (
                <div
                  key={item.key || `divider-${index}`}
                  className={styles['action-dropdown__divider']}
                />
              );
            }

            const action = item as ActionItem;
            return (
              <button
                key={action.key || index}
                className={classNames(styles['action-dropdown__item'], {
                  [styles['action-dropdown__item--danger']]: action.danger,
                  [styles['action-dropdown__item--display']]:
                    !action.onClick || typeof action.onClick !== 'function',
                })}
                onClick={() => handleActionClick(action)}
                title={action.title}
                type="button"
              >
                {action.icon && (
                  <span className={styles['action-dropdown__icon']}>
                    {action.icon}
                  </span>
                )}
                <span className={styles['action-dropdown__label']}>
                  {action.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActionDropdown;
